import { useEffect } from "react";
import { format } from "date-fns";
import { SCHEMA_NAMES } from "instantdb/types";
import { createFileRoute } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { PATHS } from "@/config/paths";
import { useMobile } from "@/providers/mobileProvider";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { Button } from "@/components/ui/button";
import { ResourceDropdown } from "@/components/dropdowns/ResourceDropdown";
import { EmojiIconPicker } from "@/components/ui/emoji-icon-picker";
import { DEFAULT_SYMBOL } from "@/config/constants";
import { tryCatch } from "@/lib/try-catch";
import { useGoal } from "@/hooks/goals/useGoal";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import { upsertGoal } from "instantdb/client/goal";
import { Container } from "@/components/ui/container";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/goal/$id/")({
  component: GoalDetailPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
});

function GoalDetailPage() {
  const { id: goalId } = Route.useParams();
  const router = useRouter();
  const { isMobile } = useMobile();
  const { setMobileNavbar } = useMobileNavbar();
  const { goal, isLoading } = useGoal(goalId as string);

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      setMobileNavbar({
        title: `${goal?.title}`,
        showBackButton: true,
      });
    }
  }, [isMobile, setMobileNavbar, goal]);

  // Handle navigation when goal is not found
  useEffect(() => {
    if (!isLoading && !goal) {
      router.navigate({ to: PATHS.GOALS });
    }
  }, [isLoading, goal, router]);

  if (isLoading) return null;

  if (!goal) return null;

  const handleEmojiSelect = async (emoji: string) => {
    const { error } = await tryCatch(
      upsertGoal({
        ...goal,
        symbol: emoji,
      }),
    );

    if (error) {
      console.error("Error updating goal:", error);
    }
  };

  return (
    <Container>
      {!isMobile && (
        <div className="col-span-12 flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => router.navigate({ to: PATHS.GOALS })}
          >
            <ArrowLeft />
          </Button>
        </div>
      )}

      <div className="col-span-12 rounded-xl shadow-sm bg-background border bg-primary/5">
        <div className="flex items-start justify-between gap-6 p-6">
          <div className="flex items-start gap-2 md:gap-5">
            <EmojiIconPicker
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex items-center justify-center text-2xl min-w-12 min-h-12 md:min-w-16 md:min-h-16 text-4xl md:text-5xl focus-visible:ring-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {goal.symbol && goal.symbol.length > 0 ? goal.symbol : DEFAULT_SYMBOL}
                </Button>
              }
              onEmojiSelect={handleEmojiSelect}
            />
            <div className="flex flex-col gap-3">
              <div>
                <Heading level="h1" size="h5" className="font-semibold text-foreground">
                  {goal.title}
                </Heading>
                <p className="text-muted-foreground max-w-2xl">{goal.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                {/* Target date pill */}
                {goal.targetDate && (
                  <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                    <Calendar className="h-3.5 w-3.5 text-primary/70" />
                    <span className="text-xs font-medium">
                      {format(new Date(goal.targetDate), "MMM d, yyyy")}
                    </span>
                  </span>
                )}

                {/* Completion date pill */}
                {goal.completionDate && (
                  <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                    <Clock className="h-3.5 w-3.5 text-primary/70" />
                    <span className="text-xs font-medium">
                      {format(new Date(goal.completionDate), "MMM d, yyyy")}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <ResourceDropdown
            align="end"
            side="bottom"
            className="mt-0"
            slug={SCHEMA_NAMES.goals}
            resource={goal}
          />
        </div>
      </div>
    </Container>
  );
}
